import JSZip from 'jszip'
import { MUTATION_TYPE, ACTION_TYPE, GETTER_TYPE } from './types'
import ServerApi from '@/api/training/server-api'
import Course from '@/api/training/course'
import Panel from '@/api/training/panel'
import ElementsCollection from '@/api/training/elements'
import Resources from '@/api/training/resources'
import Teacher from '@/api/training/teacher'
import { ErrorSeverity } from '@/api/training/error'

/* eslint-disable no-param-reassign */

function getEmptyTrainingState() {
  return {
    trainingContent: null,
    trainingRole: null,
    trainingStep: 0,
    trainingStage: null,
    errors: {},
    course: null,
    elements: null,
    resources: null,
    panels: [],
    selectedPanel: 0,
    trainingGroup: null,
    trainingMode: null,
    trainingSettings: null,
    trainings: [],
    trainingPagination: {
      pages: 0,
      total: 0
    },
    files: {},
    parsedPanels: null,
    trainingId: null,
    trainingAllRoles: [],
    stepNumberForGroupTraining: -1,
    marks: [],
    isFirstCreatingTrainingStage: 0
  }
}

function getBase64Image(img) {
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0)

  const dataURL = canvas.toDataURL('image/png')

  return dataURL.replace(/^data:image\/(png|jpg);base64,/, '')
}

/**
 * Перевод {@link JSONWrapper} в объект для редактора
 *
 * @param {JSONWrapper} object
 */
function makeFile(object) {
  return {
    JSON: object.toMapJSON(),
    JavaScript: object.getCodeString(object.codeAccessor)
  }
}

const defaultTraining = (name) => ({
  // $schema: 'https://gazprom-classes.etu.ru/api/files/json/course.schema.json',
  name,
  roles: [],
  stages: [],
  code: []
})

const defaultElements = () => ({
  // $schema: 'https://gazprom-classes.etu.ru/api/files/json/elements.schema.json',
  items: [],
  code: []
})

const defaultPanel = (id, name) => ({
  // $schema: 'https://gazprom-classes.etu.ru/api/files/json/elements.schema.json',
  items: [],
  id,
  name,
  tools: []
})

/**
 * Модуль для работы с тренажерами
 * @name training
 * @module training
 */
export default {
  /**
   * Объект store
   * @type {object}
   * @name state
   * @property {Object} trainingContent Результат запроса к серверу
   * @property {string} trainingRole TODO
   * @property {string} trainingStep TODO
   * @property {Object.<string, Array.<ValidationError>>} errors ошибки валидации
   * @property {Course} course Загруженный курс
   * @property {ElementsCollection} elements Элементы
   * @property {Array.<Panel>} panels Пульты
   * @property {Resources} resources Ресурсы
   * @property {number} selectedPanel TODO
   * @property {null} trainingGroup TODO
   * @property {null} trainingMode TODO
   * @property {Array.<Object>} trainings полученные тренажеры
   * @property {Object} trainingPagination параметры пагинации
   * @property {Set} parsedPanels id панелей, полученных с сервера.
   * @property {Array} files файлы для редактирования в редакторе кода
   * Необходим для отправки запроса на удаление
   */
  state: {
    ...getEmptyTrainingState()
  },
  /**
   * Мутации модуля работы с тренажерами
   * @name mutations
   * @mutator {Object} setTrainingContent=trainingContent
   * @mutator {} parseTrainingContent=.
   * Создает объекты классов {@link Course}, {@link Panel},
   * {@link ElementsCollection} по trainingContent
   * @mutator {Array} setTrainings=trainings
   * @mutator {Array} pushTrainings=trainings добавляет уникальные
   * тренажеры в список
   * @mutator {Object} setTrainingPagination=trainingPagination
   * @mutator {Array} setPanels=panels
   * @mutator {} setDefaultJsons=course,elements Устанавливает стандартные json'ы
   * в trainingContent. Необходимо, т.к. только что созданные тренажер приходит пустой.
   * @mutator {string} setTrainingRole=trainingRole TODO
   * @mutator {number} setTrainingStep=trainingStep TODO
   * @mutator {string} setTrainingGroup=trainingGroup TODO
   * @mutator {} clearTrainingContent=. Очистить store
   * @mutator {Object} addPanel=panel Создает новый пустой {@link Panel}
   * @mutator {Object} updatePanel=panels Обновить информацию о панели по индексу
   * @mutator {number} deletePanel=panels
   * @mutator {} runValidation=errors Валидация для всех объектов тренажера
   * (запуск validateObject для {@link Course}, {@link ElementsCollection}, {@link Panel})
   */
  mutations: {
    [MUTATION_TYPE.setIsFirstCreatingTrainingStage]: (
      state,
      isFirstCreatingTrainingStage,
    ) => {
      state.isFirstCreatingTrainingStage = isFirstCreatingTrainingStage
    },
    [MUTATION_TYPE.setTrainingContent]: (state, trainingContent) => {
      state.trainingContent = trainingContent
    },
    [MUTATION_TYPE.parseTrainingContent]: (state) => {
      state.course = new Course(state.trainingContent.course)
      state.course.name = state.trainingContent.contentObject.title || ''
      state.elements = new ElementsCollection(state.trainingContent.elements)
      state.panels = []
      state.trainingContent.panels.forEach((panel) => {
        const panelInstance = new Panel(panel.info, state.elements)
        panelInstance.databaseId = panel.id
        state.panels.push(panelInstance)
      })
      state.parsedPanels = new Set(
        state.panels.map((panel) => panel.databaseId),
      )
      state.resources = new Resources(state.trainingContent.resources)
      delete state.trainingContent.course
      delete state.trainingContent.elements
      delete state.trainingContent.panels
      delete state.trainingContent.resources
    },
    [MUTATION_TYPE.runValidation]: (state, params) => {
      params = params || {}
      state.errors = {
        Курс: state.course ? state.course.safeValidateObject(null, params) : [],
        Элементы: state.elements
          ? state.elements.safeValidateObject(null, params)
          : []
      }
      if (state.panels) {
        state.panels.forEach((panel) => {
          state.errors[`Пульт {id: ${panel.id}}`] = panel.safeValidateObject(
            null,
            params,
          )
        })
      }
    },
    [MUTATION_TYPE.setErrorList]: (state, { scope, errors }) => {
      state.errors[scope] = errors
    },
    [MUTATION_TYPE.objectsToFiles]: (state) => {
      state.files = {
        Курс: makeFile(state.course),
        Элементы: makeFile(state.elements)
      }
      state.panels.forEach((panel) => {
        state.files[`Пульт {id: ${panel.id}}`] = makeFile(panel)
      })
      Object.entries(state.resources.tilesets).forEach(([id, tileset]) => {
        state.files[`Тайлсет {id: ${id}}`] = {
          JSON: tileset.toMapJSON()
        }
      })
    },
    [MUTATION_TYPE.setFile]: (state, { group, file, newContent }) => {
      state.files[group][file] = newContent
    },
    [MUTATION_TYPE.setTrainings]: (state, trainings) => {
      state.trainings = trainings
    },
    [MUTATION_TYPE.pushTrainings]: (state, trainings) => {
      const trainingIds = {}
      state.trainings.forEach((training) => {
        trainingIds[training.id] = 1
      })
      trainings.forEach((training) => {
        if (!(training.id in trainingIds)) {
          state.trainings.push(training)
        }
      })
    },
    [MUTATION_TYPE.setTrainingPagination]: (state, pagination) => {
      state.trainingPagination = pagination
    },
    [MUTATION_TYPE.setPanels]: (state, panels) => {
      state.panels = panels
    },
    [MUTATION_TYPE.setDefaultJsons]: (state) => {
      state.trainingContent.course = defaultTraining()
      state.trainingContent.elements = defaultElements()
    },
    [MUTATION_TYPE.setTrainingRole]: (state, trainingRole) => {
      state.trainingRole = trainingRole
    },
    [MUTATION_TYPE.setTrainingStep]: (state, trainingStage) => {
      state.trainingStage = trainingStage
    },
    [MUTATION_TYPE.setCurrentStep]: (state, trainingStep) => {
      state.trainingStep = trainingStep
    },
    [MUTATION_TYPE.setSelectedPanel]: (state, panel) => {
      state.selectedPanel = panel
    },
    [MUTATION_TYPE.setStepNumberForGroupTraining]: (
      state,
      stepNumberForGroupTraining,
    ) => {
      state.stepNumberForGroupTraining = stepNumberForGroupTraining
    },
    [MUTATION_TYPE.setTrainingGroup]: (state, trainingGroup) => {
      state.trainingGroup = trainingGroup
    },
    [MUTATION_TYPE.setTrainingMode]: (state, trainingMode) => {
      state.trainingMode = trainingMode
    },
    [MUTATION_TYPE.setTrainingSettings]: (state, trainingSettings) => {
      state.trainingSettings = trainingSettings
    },
    [MUTATION_TYPE.setTrainingId]: (state, trainingId) => {
      state.trainingId = trainingId
    },
    [MUTATION_TYPE.setTrainingAllRoles]: (state, trainingAllRoles) => {
      state.trainingAllRoles = trainingAllRoles
    },
    [MUTATION_TYPE.clearTrainingContent]: (state) => {
      Object.assign(state, getEmptyTrainingState())
    },
    [MUTATION_TYPE.addPanel]: (state, { id, name }) => {
      state.panels.push(new Panel(defaultPanel(id, name), state.elements))
    },
    [MUTATION_TYPE.updatePanel]: (state, { index, newPanelData }) => {
      Object.assign(state.panels[index], newPanelData)
    },
    [MUTATION_TYPE.deletePanel]: (state, index) => {
      state.panels.splice(index, 1)
    },
    [MUTATION_TYPE.setMarks]: (state, marks) => {
      state.marks = marks
    }
  },
  actions: {
    /**
     * Получить список тренажеров с сервера
     *
     * @action fetchTrainingList=trainings
     * @param {} context
     * @param {Object} pages
     * @param {number} pages.perPage
     * @param {number} pages.pageNumber
     * @param {Array} pages.filters
     * @param {Array} pages.filters
     */
    [ACTION_TYPE.fetchTrainingList]: async (context, pages) => {
      const response = await ServerApi.getTrainingList(
        pages.perPage,
        pages.pageNumber,
        pages.filters,
        pages.sorting,
      )

      if (pages.pageNumber > 1) {
        context.commit('pushTrainings', response.data.docs)
      } else {
        context.commit('setTrainings', response.data.docs)
      }
      context.commit('setTrainingPagination', {
        total: response.data.total,
        pages: response.data.pages
      })
    },
    /**
     * Получить контент тренажера
     *
     * @action fetchTrainingContent=.
     * @param {number} id id тренажера
     */
    [ACTION_TYPE.fetchTrainingContent]: async (context, id) => {
      const response = await ServerApi.getTrainingContent(id)
      context.commit('setTrainingContent', response.data)
      if (!context.getters.getTrainingContent.course) {
        context.commit('setDefaultJsons')
      }
      context.commit('parseTrainingContent')
      context.commit(
        'content/setContentData',
        {
          contentObject: response.data.contentObject
        },
        { root: true },
      )
    },
    /**
     * Отправить запрос на добавление тренажера
     * @action addTraining=.
     */
    [ACTION_TYPE.addTraining]: async () => {
      await ServerApi.addTraining()
    },
    /**
     * Отправить запрос на удаление тренажера
     * @action deleteTraining=.
     * @param {} context
     * @param {number} id id тренажера
     */
    [ACTION_TYPE.deleteTraining]: async (context, id) => {
      await ServerApi.deleteTraining(id)
    },
    [ACTION_TYPE.validateObjects]: ({ getters, commit }) => {
      const params = getters.getValidationInfo
      commit('runValidation', params)
    },
    [ACTION_TYPE.updateTrainingContent]: async (context) => {
      const request = {
        contentObject: context.rootGetters['content/getContent'].contentObject,
        course: context.getters.getTrainingCourse.object,
        elements: context.getters.getElements.object,
        panels: context.getters.getPanels.map((panel) => ({
          info: panel.object,
          id: panel.databaseId
        })),
        resources: context.getters.getResources.resourcesToSend
      }
      const { id } = context.getters.getTrainingContent
      const panelIds = new Set(
        context.getters.getPanels.map((panel) => (panel.databaseId ? panel.databaseId : -1)),
      )
      await Promise.all([
        ...Array.from(context.getters.getParsedPanels)
          .filter(
            (databaseId) => !panelIds.has(databaseId) && databaseId !== undefined,
          )
          .map((databaseId) => ServerApi.deletePanel(id, databaseId)),
        ...Array.from(context.getters.getResources.resourcesToDelete).map(
          (resourceId) => ServerApi.deleteResource(id, resourceId),
        )
      ])
      await Promise.all([
        ServerApi.updateTraining(id, request)
        // context.dispatch('content/saveContent', request.contentObject, {
        //   root: true,
        // }),
      ])
    },
    [ACTION_TYPE.zipCourse]: async ({ commit, getters }) => {
      const zip = new JSZip()
      commit('objectsToFiles')
      Object.entries(getters.getFilesList).forEach(([name, object]) => {
        zip.file(`json/${name}.json`, object.JSON.json)
        if (object.JavaScript) {
          zip.file(`js/${name}.js`, object.JavaScript)
        }
      })
      Object.entries(getters.getResources.imagesById).forEach(([id, img]) => {
        const name = `images/{id: ${id}} ${
          getters.getResources.texturesById[id].file.name
        }`
        zip.file(name, getBase64Image(img), { base64: true })
      })
      Object.entries(getters.getResources.tilesets).forEach(([id, tileset]) => {
        const name = `images (tilesets)/{id: ${id}} ${tileset.file.file.name}`
        zip.file(name, getBase64Image(tileset.image), { base64: true })
      })
      Object.entries(getters.getResources.audioBlobById).forEach(
        ([id, blob]) => {
          const file = getters.getResources.audioFilesById[id]
          const name = `audio/{id: ${id}} ${file.file.name}`
          zip.file(name, blob)
        },
      )
      return zip.generateAsync({ type: 'blob' })
    },
    [ACTION_TYPE.fetchMarks]: async ({ commit }, simId) => {
      const marks = await Teacher.getPersonMarks(simId)
      commit('setMarks', marks.data)
    }
  },
  /**
   * Геттеры модуля
   * @type {object}
   * @name getters
   * @getter {Array} getTrainingContent=trainingContent
   * @getter {Object} getTrainings=trainings
   * @getter {string} getTrainingRole=trainingRole
   * @getter {string} getTrainingStep=trainingStep
   * @getter {number} getSelectedPanel=selectedPanel
   * @getter {Array.<Panel>} getPanels=panels
   * @getter {Course} getTrainingCourse=course
   * @getter {string} getTrainingGroup=trainingGroup
   * @getter {string} getTrainingMode=trainingMode
   * @getter {Object} getTrainingPagination=trainingPagination
   * @getter {ElementsCollection} getElements=element
   * @getter {boolean} getIsTrainingInitialized=. Загружен ли тренажер
   * @getter {Object} getErrors=errors
   * @getter {boolean} hasErrors=. Возвращает true, если есть хотя бы одна ошибка.
   * В этом случае рендеринг невозможен.
   * @getter {Array.<number>} getParsedPanels=parsedPanels
   * @getter {Resources} getResources=resources
   */
  getters: {
    [GETTER_TYPE.getTrainings]: (state) => state.trainings,
    [GETTER_TYPE.getTrainingContent]: (state) => state.trainingContent,
    [GETTER_TYPE.getTrainingRole]: (state) => state.trainingRole,
    [GETTER_TYPE.getTrainingStep]: (state) => state.trainingStage,
    [GETTER_TYPE.getCurrentStep]: (state) => state.trainingStep,
    [GETTER_TYPE.getSelectedPanel]: (state) => state.selectedPanel,
    [GETTER_TYPE.getPanels]: (state) => state.panels,
    [GETTER_TYPE.getTrainingCourse]: (state) => state.course,
    [GETTER_TYPE.getTrainingGroup]: (state) => state.trainingGroup,
    [GETTER_TYPE.getStepNumberForGroupTraining]: (state) => state.stepNumberForGroupTraining,
    [GETTER_TYPE.getTrainingMode]: (state) => state.trainingMode,
    [GETTER_TYPE.getTrainingSettings]: (state) => state.trainingSettings,
    [GETTER_TYPE.getTrainingId]: (state) => state.trainingId,
    [GETTER_TYPE.getTrainingAllRoles]: (state) => state.trainingAllRoles,
    [GETTER_TYPE.getTrainingPagination]: (state) => state.trainingPagination,
    [GETTER_TYPE.getElements]: (state) => state.elements,
    [GETTER_TYPE.getIsTrainingInitialized]: (state) => state.trainingContent
      && state.course && state.elements,
    [GETTER_TYPE.getErrors]: (state) => state.errors,
    [GETTER_TYPE.hasErrors]: (state) => Object.values(state.errors).some((scope) => scope
      .some((error) => error.severity === ErrorSeverity.ERROR)),
    [GETTER_TYPE.getParsedPanels]: (state) => state.parsedPanels,
    [GETTER_TYPE.getFilesList]: (state) => state.files,
    [GETTER_TYPE.getFileObject]: (state) => (group) => {
      if (group === 'Курс') {
        return state.course
      }
      if (group === 'Элементы') {
        return state.elements
      }
      if (group.startsWith('Тайлсет')) {
        const tilesetId = Number(group.substring(13, group.length - 1))
        return state.resources.tilesets[tilesetId]
      }
      const panelId = Number(group.substring(11, group.length - 1))
      return state.panels.find((p) => p.id === panelId)
    },
    [GETTER_TYPE.getValidationInfo]: (state) => {
      const panelsById = {}
      state.panels.forEach((panel) => {
        panelsById[panel.id] = panel
      })
      return {
        panelIds: state.panels.map((panel) => panel.id),
        panelsById,
        globalItems: state.elements.itemsById,
        resources: state.resources
      }
    },
    [GETTER_TYPE.getResources]: (state) => state.resources,
    [GETTER_TYPE.getIsFirstCreatingTrainingStage]: (state) => state.isFirstCreatingTrainingStage,
    [GETTER_TYPE.getMarks]: (state) => state.marks
  }
}
